import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

// Fallback sigur:
// 1. Dacă ai REACT_APP_BACKEND_URL, îl folosește
// 2. Dacă nu ai, folosește același domeniu
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  const verifyToken = useCallback(async () => {
    if (!token) {
      setTokenValid(false);
      setVerifying(false);
      return;
    }

    try {
      console.log('ResetPassword verifyToken');
      console.log('BACKEND_URL:', BACKEND_URL);
      console.log('API:', API);
      console.log('token:', token);

      const response = await axios.get(`${API}/auth/verify-reset-token/${token}`, {
        timeout: 15000,
      });

      console.log('verify reset token response:', response.data);

      setEmail(response?.data?.email || '');
      setTokenValid(true);
    } catch (error) {
      console.error('verify reset token error:', error);
      console.error('verify reset token status:', error?.response?.status);
      console.error('verify reset token data:', error?.response?.data);

      setTokenValid(false);

      const detail =
        error?.response?.data?.detail ||
        t('invalidResetLink') ||
        'Invalid or expired reset link';

      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  }, [token, t]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error(t('invalidResetLink') || 'Invalid reset link');
      return;
    }

    if (!password.trim()) {
      toast.error(t('enterNewPassword') || 'Enter a new password');
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('passwordsDoNotMatch') || 'Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error(t('passwordTooShort') || 'Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/auth/reset-password`,
        {
          token,
          new_password: password,
        },
        {
          timeout: 15000,
        }
      );

      setSuccess(true);
      toast.success(t('passwordResetSuccessful') || 'Password reset successful');

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('reset password error:', error);
      console.error('reset password status:', error?.response?.status);
      console.error('reset password data:', error?.response?.data);

      toast.error(
        error?.response?.data?.detail ||
          t('failedToResetPassword') ||
          'Failed to reset password'
      );
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CosmicBackground />
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto mb-4" />
          <p className="text-slate-400">
            {t('verifyingResetLink') || 'Verifying reset link...'}
          </p>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CosmicBackground />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 glass-card p-8 max-w-md w-full mx-4 text-center"
        >
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('invalidResetLink') || 'Invalid reset link'}
          </h2>
          <p className="text-slate-400 mb-6">
            {t('resetLinkExpired') || 'This password reset link is invalid or has expired.'}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/forgot-password')}
              className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full hover:from-violet-500 hover:to-purple-500 transition-all"
            >
              {t('sendNewResetLink') || 'Send New Reset Link'}
            </button>

            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 border border-white/10 text-white rounded-full hover:bg-white/5 transition-all"
            >
              {t('backToLogin') || 'Back to Login'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CosmicBackground />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 glass-card p-8 max-w-md w-full mx-4 text-center"
        >
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('passwordReset') || 'Password Reset'}
          </h2>
          <p className="text-slate-400">
            {t('redirectingToLogin') || 'Redirecting to login...'}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <CosmicBackground />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 glass-card p-8 max-w-md w-full mx-4"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" />
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">
            {t('setNewPassword') || 'Set New Password'}
          </h2>

          <p className="text-slate-400 text-sm break-words">
            {(t('settingPasswordFor') || 'Setting password for') + ' '}
            <span className="text-white">{email}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-slate-300 text-sm mb-2">
              {t('newPassword') || 'New Password'}
            </label>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800/50 border border-white/10 rounded-xl text-white focus:outline-none focus:border-violet-500 transition-colors pr-12"
                placeholder={t('enterNewPassword') || 'Enter new password'}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="password-input"
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-slate-300 text-sm mb-2">
              {t('confirmPassword') || 'Confirm Password'}
            </label>

            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800/50 border border-white/10 rounded-xl text-white focus:outline-none focus:border-violet-500 transition-colors"
              placeholder={t('confirmPassword') || 'Confirm password'}
              required
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:from-violet-500 hover:to-purple-500 transition-all disabled:opacity-50"
            data-testid="reset-password-btn"
          >
            {loading
              ? t('resetting') || 'Resetting...'
              : t('setNewPassword') || 'Set New Password'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
